import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../AuthContext';
import { useToast } from './Toast';
import {
  getUsers,
  createUser,
  updateUser,
  deactivateUser,
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  getPermissions,
} from '../api';

interface UserItem {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_admin: boolean;
  role_id: string | null;
  role_name: string | null;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  user_count: number;
}

interface PermissionGroups {
  [domain: string]: string[];
}

interface PermRow {
  label: string;
  sub?: boolean;
  view?: string;
  create?: string;
  edit?: string;
  delete?: string;
}

// Note: for the read-oriented data sections the "Create" column is reused as
// "Sync/Pull" (there is nothing to create), and each report section exposes an
// "Export" capability as a sub-row.
const PERM_ROWS: PermRow[] = [
  { label: 'Amazon Accounts',  view: 'accounts.view',          create: 'accounts.create',      edit: 'accounts.edit',  delete: 'accounts.delete' },
  { label: 'Orders',           view: 'orders.view',            create: 'orders.sync' },
  { label: 'Export',           sub: true,                       create: 'orders.export' },
  { label: 'Inventory',        view: 'inventory.view',         create: 'inventory.sync' },
  { label: 'Export',           sub: true,                       create: 'inventory.export' },
  { label: 'Business Reports', view: 'business_reports.view',  create: 'business_reports.sync' },
  { label: 'Export',           sub: true,                       create: 'business_reports.export' },
  { label: 'Scheduler',        view: 'scheduler.view',                                          edit: 'scheduler.edit' },
  { label: 'User Management',  view: 'users.view',             create: 'users.create',         edit: 'users.edit',     delete: 'users.delete' },
  { label: 'Settings',         view: 'settings.view',                                           edit: 'settings.edit' },
];

const ROLE_COLORS: Record<string, string> = {
  Manager: 'role-admin',
  Member: 'role-member',
  Viewer: 'role-viewer',
};

function getInitials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function AvatarInitials({ name, size = 36 }: { name: string; size?: number }) {
  const initials = getInitials(name);
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div
      className="um-avatar"
      style={{ width: size, height: size, background: colors[idx], fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [tab, setTab] = useState<'users' | 'roles'>('users');
  const [users, setUsers] = useState<UserItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permGroups, setPermGroups] = useState<PermissionGroups>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRoleId, setNewRoleId] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);

  // Three-dot menu — rendered as a portal to escape overflow:hidden on .um-card
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, userId: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(userId);
  };

  useEffect(() => {
    if (!openMenuId) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.um-dropdown-portal')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenuId]);

  // Edit user modal
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  const openEditUser = (u: UserItem) => {
    setEditingUser(u);
    setEditFullName(u.full_name);
    setEditEmail(u.email);
    setEditPassword('');
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setSavingUser(true);
    setError('');
    try {
      await updateUser(editingUser.id, {
        full_name: editFullName,
        email: editEmail,
        ...(editPassword ? { new_password: editPassword } : {}),
      });
      setEditingUser(null);
      showToast('User updated', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  // Role modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [savingRole, setSavingRole] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        getUsers(),
        getRoles(),
        getPermissions(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setPermGroups(permsRes.data.groups || {});
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── User actions ───────────────────────────────────────────────────────────

  const handleRoleChange = async (userId: string, newRoleId: string) => {
    try {
      await updateUser(userId, { role_id: newRoleId || null });
      showToast('Role updated', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggleAdmin = async (u: UserItem) => {
    if (u.id === currentUser?.id) {
      setError("You cannot change your own admin status");
      return;
    }
    try {
      await updateUser(u.id, { is_admin: !u.is_admin });
      showToast(u.is_admin ? 'Admin access revoked' : 'Admin access granted', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (u: UserItem) => {
    if (u.id === currentUser?.id) {
      setError("You cannot deactivate yourself");
      return;
    }
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      showToast(u.is_active ? 'User deactivated' : 'User activated', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingUser(true);
    setError('');
    try {
      await createUser({
        email: newEmail,
        full_name: newFullName,
        password: newPassword,
        role_id: newIsAdmin ? undefined : (newRoleId || undefined),
        is_admin: newIsAdmin,
      });
      setShowCreateUser(false);
      setNewEmail('');
      setNewFullName('');
      setNewPassword('');
      setNewRoleId('');
      setNewIsAdmin(false);
      showToast('User created', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  // ── Role actions ───────────────────────────────────────────────────────────

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setRolePermissions([]);
    setShowRoleModal(true);
  };

  const openEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setRolePermissions([...role.permissions]);
    setShowRoleModal(true);
  };

  const togglePermission = (perm: string) => {
    setRolePermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const toggleDomain = (domain: string, perms: string[]) => {
    const allSelected = perms.every((p) => rolePermissions.includes(p));
    if (allSelected) {
      setRolePermissions((prev) => prev.filter((p) => !perms.includes(p)));
    } else {
      setRolePermissions((prev) => Array.from(new Set([...prev, ...perms])));
    }
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingRole(true);
    setError('');
    try {
      if (editingRole) {
        await updateRole(editingRole.id, {
          name: roleName,
          description: roleDescription || undefined,
          permissions: rolePermissions,
        });
      } else {
        await createRole({
          name: roleName,
          description: roleDescription || undefined,
          permissions: rolePermissions,
        });
      }
      setShowRoleModal(false);
      showToast(editingRole ? 'Role updated' : 'Role created', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to save role');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await deleteRole(role.id);
      showToast('Role deleted', 'success');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete role');
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (!currentUser?.is_admin) {
    return (
      <div className="page-content">
        <div className="error-banner">Only administrators can access user management.</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h2>User Management</h2>
          <p className="page-subtitle">Manage users and access roles</p>
        </div>
        {tab === 'users' && (
          <button className="btn btn-primary" onClick={() => setShowCreateUser(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            Create User
          </button>
        )}
        {tab === 'roles' && (
          <button className="btn btn-primary" onClick={openCreateRole}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Create Role
          </button>
        )}
      </div>

      {/* ── Stats row ────────────────────────────────────────────── */}
      {!loading && (
        <div className="um-stats-row">
          <div className="um-stat-card">
            <div className="um-stat-value">{users.filter(u => u.is_active).length}</div>
            <div className="um-stat-label">Active Users</div>
          </div>
          <div className="um-stat-card">
            <div className="um-stat-value">{users.filter(u => u.is_admin).length}</div>
            <div className="um-stat-label">Admins</div>
          </div>
          <div className="um-stat-card">
            <div className="um-stat-value">{roles.length}</div>
            <div className="um-stat-label">Roles Defined</div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16, cursor: 'pointer' }} onClick={() => setError('')}>
          {error}
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="tab-bar" style={{ marginBottom: 20 }}>
        <button className={`tab-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          Users ({users.length})
        </button>
        <button className={`tab-btn ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}>
          Roles ({roles.length})
        </button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" />Loading…</div>
      ) : tab === 'users' ? (
        <>
          {/* ── Create user modal ─────────────────────────────────── */}
          {showCreateUser && (
            <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
              <div className="role-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
                <div className="role-modal-header">
                  <div className="role-modal-header-left">
                    <div className="role-modal-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                    </div>
                    <div>
                      <h3>Create User</h3>
                      <p>Add a new user to the system</p>
                    </div>
                  </div>
                  <button className="modal-close" onClick={() => setShowCreateUser(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <form onSubmit={handleCreateUser}>
                  <div className="role-modal-body">
                    <div className="role-modal-section">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input type="text" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} required placeholder="John Smith" />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required placeholder="john@company.com" />
                      </div>
                      <div className="form-group">
                        <label>Password *</label>
                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} placeholder="Min. 6 characters" />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input type="checkbox" checked={newIsAdmin} onChange={(e) => setNewIsAdmin(e.target.checked)} style={{ width: 16, height: 16 }} />
                          <span>Admin user <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(full access, can manage users & roles)</span></span>
                        </label>
                      </div>
                      {!newIsAdmin && (
                        <div className="form-group">
                          <label>Assign Role</label>
                          <select value={newRoleId} onChange={(e) => setNewRoleId(e.target.value)}>
                            <option value="">— No role (no access) —</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="role-modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateUser(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={creatingUser}>
                      {creatingUser ? 'Creating…' : 'Create User'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── Edit user modal ───────────────────────────────────── */}
          {editingUser && (
            <div className="modal-overlay" onClick={() => setEditingUser(null)}>
              <div className="role-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
                <div className="role-modal-header">
                  <div className="role-modal-header-left">
                    <div className="role-modal-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </div>
                    <div>
                      <h3>Edit User</h3>
                      <p>Update name and email address</p>
                    </div>
                  </div>
                  <button className="modal-close" onClick={() => setEditingUser(null)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <form onSubmit={handleSaveUser}>
                  <div className="role-modal-body">
                    <div className="role-modal-section">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          value={editFullName}
                          onChange={(e) => setEditFullName(e.target.value)}
                          required
                          placeholder="Full name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          required
                          placeholder="email@company.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep current)</span></label>
                        <input
                          type="password"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          minLength={6}
                          placeholder="Min. 6 characters"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="role-modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingUser}>
                      {savingUser ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── Users list ─────────────────────────────────────────── */}
          {users.length === 0 ? (
            <div className="um-card">
              <div className="um-empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p>No users yet. Create a user to get started.</p>
              </div>
            </div>
          ) : (
            <div className="um-card">
              <div className="um-table-header" style={{ gridTemplateColumns: 'minmax(0,1fr) 100px 160px 100px 48px' }}>
                <span>User</span>
                <span>Type</span>
                <span>Role</span>
                <span>Status</span>
                <span>Actions</span>
              </div>
              <div className="um-members-list">
                {users.map((u: UserItem) => (
                  <div key={u.id} className={`um-member-row ${!u.is_active ? 'um-inactive' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) 100px 160px 100px 48px' }}>
                    {/* User */}
                    <div className="um-member-identity">
                      <AvatarInitials name={u.full_name} size={38} />
                      <div className="um-member-info">
                        <span className="um-member-name">
                          {u.full_name}
                          {u.id === currentUser?.id && <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>(you)</span>}
                        </span>
                        <span className="um-member-email">{u.email}</span>
                      </div>
                    </div>
                    {/* Type */}
                    <div className="um-member-meta">
                      <span className={`role-badge ${u.is_admin ? 'role-owner' : 'role-member'}`} style={{ fontSize: 12 }}>
                        {u.is_admin ? 'Admin' : 'User'}
                      </span>
                    </div>
                    {/* Role */}
                    <div className="um-member-role-cell">
                      {u.is_admin ? (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>All permissions</span>
                      ) : (
                        <select
                          className={`um-role-select role-badge ${ROLE_COLORS[u.role_name || ''] || 'role-member'}`}
                          value={u.role_id || ''}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          style={{ fontSize: 12 }}
                        >
                          <option value="">— No role —</option>
                          {roles.map((r: Role) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    {/* Status */}
                    <div className="um-member-meta">
                      <span className={`badge ${u.is_active ? 'badge-success' : 'badge-muted'}`} style={{ fontSize: 11 }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {/* Three-dot actions */}
                    <div className="um-member-actions-cell">
                      <div className="um-action-menu">
                        <button
                          className="um-action-btn"
                          onClick={(e) => openMenuId === u.id ? setOpenMenuId(null) : openMenu(e, u.id)}
                          title="Actions"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Three-dot portal dropdown ─────────────────────────── */}
          {(() => {
            const u = users.find((x) => x.id === openMenuId);
            if (!u) return null;
            return ReactDOM.createPortal(
              <div
                className="um-dropdown um-dropdown-portal"
                style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
              >
                <button
                  className="um-dropdown-item"
                  onClick={() => { setOpenMenuId(null); openEditUser(u); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit details
                </button>
                <button
                  className="um-dropdown-item"
                  disabled={u.id === currentUser?.id}
                  onClick={() => { setOpenMenuId(null); handleToggleAdmin(u); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  {u.is_admin ? 'Revoke admin' : 'Grant admin'}
                </button>
                <div className="um-dropdown-divider" />
                <button
                  className={`um-dropdown-item ${u.is_active ? 'danger' : ''}`}
                  disabled={u.id === currentUser?.id}
                  onClick={() => { setOpenMenuId(null); handleToggleActive(u); }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {u.is_active
                      ? <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>
                      : <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                    }
                  </svg>
                  {u.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>,
              document.body
            );
          })()}
        </>
      ) : (
        /* ── Roles tab ───────────────────────────────────────────── */
        <>
          {roles.length === 0 ? (
            <div className="um-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              <p>No roles defined yet. Create a role to assign permissions to users.</p>
            </div>
          ) : (
            <div className="um-roles-grid">
              {roles.map((role: Role) => (
                <div key={role.id} className="um-role-card">
                  <div className="um-role-card-header">
                    <div className="um-role-card-name">
                      <span className={`role-badge ${ROLE_COLORS[role.name] || 'role-member'}`}>{role.name}</span>
                    </div>
                    <div className="um-role-card-actions">
                      <button className="btn btn-secondary btn-small" onClick={() => openEditRole(role)}>Edit</button>
                      <button
                        className="btn btn-danger btn-small"
                        onClick={() => handleDeleteRole(role)}
                        disabled={role.user_count > 0}
                        title={role.user_count > 0 ? 'Cannot delete a role with active users' : ''}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="um-role-card-body">
                    {role.description && <p className="um-role-description">{role.description}</p>}
                    <div className="um-role-stats">
                      <span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
                        {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
                      </span>
                      <span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                        {role.permissions.length} permissions
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Role create/edit modal ──────────────────────────── */}
          {showRoleModal && (
            <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
              <div className="role-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
                <div className="role-modal-header">
                  <div className="role-modal-header-left">
                    <div className="role-modal-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div>
                      <h3>{editingRole ? 'Edit Role' : 'Create Role'}</h3>
                      <p>Configure role name and permissions</p>
                    </div>
                  </div>
                  <button className="modal-close" onClick={() => setShowRoleModal(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <form onSubmit={handleSaveRole}>
                  <div className="role-modal-body">
                    {/* Basic info */}
                    <div className="role-modal-section">
                      <div className="role-modal-section-label">Basic Information</div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Role Name *</label>
                          <input
                            type="text"
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            required
                            maxLength={100}
                            placeholder="e.g. Content Manager"
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <input
                            type="text"
                            value={roleDescription}
                            onChange={(e) => setRoleDescription(e.target.value)}
                            maxLength={255}
                            placeholder="Optional description"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Permissions matrix */}
                    <div className="role-modal-section">
                      <div className="role-modal-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Permissions</span>
                        <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>{rolePermissions.length} selected</span>
                      </div>
                      <table className="perm-matrix">
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Section</th>
                            <th>View</th>
                            <th>Create</th>
                            <th>Edit</th>
                            <th>Delete</th>
                            <th className="perm-full-col">Full</th>
                          </tr>
                        </thead>
                        <tbody>
                          {PERM_ROWS.map((row) => {
                            const rowPerms = [row.view, row.create, row.edit, row.delete].filter(Boolean) as string[];
                            const allChecked = rowPerms.length > 0 && rowPerms.every((p) => rolePermissions.includes(p));
                            const toggleFull = () => {
                              if (allChecked) {
                                setRolePermissions((prev) => prev.filter((p) => !rowPerms.includes(p)));
                              } else {
                                setRolePermissions((prev) => Array.from(new Set([...prev, ...rowPerms])));
                              }
                            };
                            return (
                              <tr key={row.view ?? row.create ?? row.edit ?? row.label} className={row.sub ? 'perm-sub-row' : 'perm-section-row'}>
                                <td>{row.sub ? <span className="perm-sub-label">↳ {row.label}</span> : row.label}</td>
                                {(['view', 'create', 'edit', 'delete'] as const).map((col) => {
                                  const perm = row[col];
                                  return (
                                    <td key={col}>
                                      {perm
                                        ? <input type="checkbox" checked={rolePermissions.includes(perm)} onChange={() => togglePermission(perm)} />
                                        : <span className="perm-na">—</span>}
                                    </td>
                                  );
                                })}
                                <td className="perm-full-col">
                                  {!row.sub && (
                                    <input type="checkbox" checked={allChecked} onChange={toggleFull} title="Toggle all" />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="role-modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowRoleModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingRole}>
                      {savingRole ? 'Saving…' : editingRole ? 'Save Changes' : 'Create Role'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
